import { useCallback, useEffect, useMemo, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'

import { AuthContext, type AuthContextValue } from '@/auth/auth-context'
import { useSession } from '@/auth/use-session'
import { api, setRequires2faSetupHandler, setUnauthorizedHandler } from '@/lib/api-client'
import { isRequires2fa } from '@/lib/api-error'
import { queryKeys } from '@/lib/query-keys'

export function AuthProvider({ children }: { children: ReactNode }) {
    const { data, error, isSuccess, isError, isLoading } = useSession()
    const queryClient = useQueryClient()
    const navigate = useNavigate()

    /**
     * Local teardown. Cached admin data belongs to the session that fetched it, and
     * keeping it would leak one operator's view into the next sign-in on a shared
     * machine.
     */
    const forget = useCallback(() => {
        queryClient.setQueryData(queryKeys.session, null)
        queryClient.clear()
        // The search string is part of where the operator was: list pages keep their
        // filters, page and selection in the URL. Sending the pathname alone put them
        // back on a bare list after signing in again, losing the query they had built.
        // `auth-gate.tsx` already preserves both; this matches it.
        void navigate({
            to: '/login',
            search: { redirect: `${window.location.pathname}${window.location.search}` }
        })
    }, [queryClient, navigate])

    /**
     * Signing out has to reach the server. The session lives in a cookie on the
     * parent domain and is backed by a server-side store, so dropping the local
     * cache alone would leave the operator signed in -- the next probe would restore
     * everything, which is the opposite of what the menu item promises.
     *
     * The endpoint's answer is not worth blocking on: a failure means the session was
     * already gone, and either way this browser is done with it.
     */
    const signOut = useCallback(async () => {
        try {
            await api.get('/auth/logout')
        } catch {
            // Already expired, or the network is down. Forget it locally regardless.
        }
        forget()
    }, [forget])

    // Any endpoint answering 401 means the session expired mid-session, or this
    // network has not entered a 2FA code. Both go through `forget` to `/login`,
    // where the probe runs again and the flag distinguishes them. Routing that
    // through one handler keeps the api client free of router and query imports.
    // It calls `forget` rather than `signOut`: an expired session is already gone,
    // and a network that still needs a code must keep the one it has.
    //
    // A 403 with `requires2faSetup` means 2FA was turned off under a still-cached
    // session. Re-probing lets the gate re-evaluate; the client stays free of
    // query imports the same way.
    useEffect(() => {
        setUnauthorizedHandler(forget)
        setRequires2faSetupHandler(() => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.session })
        })
        return () => {
            setUnauthorizedHandler(null)
            setRequires2faSetupHandler(null)
        }
    }, [forget, queryClient])

    const value = useMemo<AuthContextValue>(
        () => ({
            user: data ?? null,
            isReady: !isLoading && (isSuccess || isError),
            isAuthenticated: isSuccess && Boolean(data),
            requires2fa: isError && isRequires2fa(error),
            signOut
        }),
        [data, error, isSuccess, isError, isLoading, signOut]
    )

    return <AuthContext value={value}>{children}</AuthContext>
}
